import { BottomBarOptions, CommonUIEvents, ConstructorOptions, CreateOptions } from "@gluckgames/commonui";
import { MenuConfigData, Options, RealityCheckOptions, SoundState, SpinMenuConfig } from "@gluckgames/commonui";
import { Model } from "appworks/model/model";
import { Services } from "appworks/services/services";
import { gamevy } from "gamevy/gamevy";
import { gamevyModel } from "gamevy/gamevy-model";
import { Signal } from "signals";

export type CUIOptions = Partial<CreateOptions & BottomBarOptions & RealityCheckOptions & ConstructorOptions & Options & CustomCUIOptions>;

export enum GamevyAutoSpinOptions {
    Unlimited = "unlimited",
    AnyWin = "any"
}

export enum GamevyAutoSpinSpeeds {
    Normal = "normal",
    Turbo = "turbo",
    Super = "super"
}

export interface CustomCUIOptions {
    /**
     * Reset CUI's own scaling CSS and scale CUI to canvas. Default value is **false**.
     */
    customLayout?: boolean;
}

export class CommonUISettings {
    public autoplayStarted: Signal = new Signal();

    protected balance: number = 0;
    protected menuConfigData: MenuConfigData = {};

    constructor() {
        Services.transaction.balanceUpdate.add((balance: number) => {
            this.balance = balance;
        });

        gamevy.commonUI.on(CommonUIEvents.CHANGE_AUTOSPIN_SETTINGS, (data) => {
            this.updateAutoSpinSettings(data);
        });
    }

    public getAllSettings(): MenuConfigData {
        this.getAutoplaySettings(true);
        this.getSoundSettings();
        this.getRulesSettings();
        this.getHowToSettings();

        return this.menuConfigData;
    }

    public getHamburgerSettings(): MenuConfigData {
        delete this.menuConfigData.autoSpin;
        this.getSoundSettings();
        this.getRulesSettings();
        this.getHowToSettings();

        return this.menuConfigData;
    }

    public getAutoplaySettings(reset: boolean): SpinMenuConfig {
        if (reset) {
            this.setDefaultAutoplaySettings();
        }
        return this.menuConfigData.autoSpin;
    }

    public getSoundSettings(): SoundState {
        this.menuConfigData.sound = {
            musicEnabled: !Services.sound.getMusicMuted(),
            sfxEnabled: !Services.sound.getSoundFxMuted()
        };

        return this.menuConfigData.sound;
    }

    public getRulesSettings(): string | undefined {
        this.menuConfigData.rules = gamevyModel.getRulesUrl();
        return this.menuConfigData.rules;
    }

    public getHowToSettings(): string {
        const howTo = Services.translations.get("gmv.howTo");
        if (howTo && howTo !== "gmv.howTo") {
            this.menuConfigData.howTo = howTo;
        }
        return this.menuConfigData.howTo;
    }

    public parseAutoplayValue(value: string | number): number {
        if (typeof (value) === "number") {
            return value;
        } else {
            switch (value) {
                case GamevyAutoSpinOptions.AnyWin:
                    return 0;
                case GamevyAutoSpinOptions.Unlimited:
                    return Number.POSITIVE_INFINITY;
                default:
                    return -1; // Value not set or unhandled.
            }
        }
    }

    protected setDefaultAutoplaySettings(): void {
        const speedValues: GamevyAutoSpinSpeeds[] = [
            GamevyAutoSpinSpeeds.Normal
        ];

        let currentSpeed = GamevyAutoSpinSpeeds.Normal;

        if (Services.jurisdiction.getQuickSpinEnabled()) {
            speedValues.push(
                GamevyAutoSpinSpeeds.Turbo,
                GamevyAutoSpinSpeeds.Super
            );

            const { turboSpin, quickSpin } = Model.read().settings;
            if (turboSpin) {
                currentSpeed = GamevyAutoSpinSpeeds.Super;
            } else if (quickSpin) {
                currentSpeed = GamevyAutoSpinSpeeds.Turbo;
            }
        }

        const singleWinStopValues = this.getSingleWinExceeds();

        this.menuConfigData.autoSpin = {
            speedValues,
            speed: currentSpeed,
            singleWinStopValues,
            firstSingleWinStopValue: singleWinStopValues[0],
            singleWinStop: singleWinStopValues[singleWinStopValues.length - 1],
            lossLimitStopValues: this.getLossLimits(),
            firstLossLimitStopValue: GamevyAutoSpinOptions.Unlimited,
            lossLimitStop: undefined,
            numberOfAutospinValues: Services.jurisdiction.getAutoplayLimits() || [10, 20, 30, 50, 100],
            numberOfAutospins: undefined,
            discardChanges: true
        };

        if (Services.jurisdiction.isUnlimitedAutoplayEnabled()) {
            this.menuConfigData.autoSpin.lossLimitStop = GamevyAutoSpinOptions.Unlimited;
            this.menuConfigData.autoSpin.singleWinStop = GamevyAutoSpinOptions.Unlimited;
            this.menuConfigData.autoSpin.numberOfAutospins = Number.POSITIVE_INFINITY;
        }
    }

    protected getLossLimits(): Array<number | string> {
        const bet: number = Services.bet.getTotalStake();
        const multipliers: number[] = [5, 10, 20, 30, 50, 100, 200];
        const lossLimits: Array<number | string> = [];
        for (const multiplier of multipliers) {
            lossLimits.push(multiplier * bet);
        }
        lossLimits.push(GamevyAutoSpinOptions.Unlimited);
        return lossLimits;
    }

    protected getSingleWinExceeds(): Array<number | string> {
        const bet: number = Services.bet.getTotalStake();
        const multipliers: number[] = [5, 15, 30, 60, 150, 300, 500, 1500, 3000];
        const singleWinExeeds: Array<number | string> = [];
        for (const multiplier of multipliers) {
            singleWinExeeds.push(multiplier * bet);
        }
        return singleWinExeeds;
    }

    private updateAutoSpinSettings(data) {
        this.menuConfigData.autoSpin.speed = data.speed;
        this.menuConfigData.autoSpin.singleWinStop = data.singleWinStop;
        this.menuConfigData.autoSpin.lossLimitStop = data.lossLimitStop;
        this.menuConfigData.autoSpin.numberOfAutospins = data.numberOfAutospins;

        this.autoplayStarted.dispatch();
    }
}
